import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Camera, Upload, Sparkles, PlusCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { compressImage } from "../lib/compressImage";
import { toast } from "sonner";

export const TREATMENT_CATEGORIES = [
  "Topical steroid",
  "Emollient",
  "Dressing",
  "Soap substitute",
  "Oral medication",
  "Topical antibiotic",
  "Antifungal",
  "Biologic",
  "Other",
];

export default function AddTreatmentDialog({
  onAdd,
  title = "Add a prescription",
  triggerLabel = "Add a prescription",
  triggerLabelMore = "Add another",
  hasItems = false,
  testidPrefix = "rx",
  triggerClass = "border-sage-300 text-sage-700 hover:bg-sage-50",
  primaryClass = "bg-sage-400 hover:bg-sage-500 text-white",
  activeTabClass = "data-[state=active]:bg-sage-400 data-[state=active]:text-white",
  scanCardClass = "border-sage-200 bg-sage-50 hover:bg-sage-100",
  scanIconClass = "text-sage-500",
  scanTextClass = "text-sage-700",
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("scan");

  // Scan tab
  const [analyzing, setAnalyzing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanResult, setScanResult] = useState(null); // {name, category}
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  // Manual tab
  const [manualName, setManualName] = useState("");
  const [manualCategory, setManualCategory] = useState(TREATMENT_CATEGORIES[0]);

  const reset = () => {
    setTab("scan");
    setAnalyzing(false);
    setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
    setScanResult(null);
    setManualName("");
    setManualCategory(TREATMENT_CATEGORIES[0]);
  };

  const handleFile = async (files) => {
    const raw = files?.[0];
    if (!raw) return;
    const f = await compressImage(raw);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
    setScanResult(null);
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/treatments/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScanResult({ name: data.name || "", category: data.category || "Other" });
      toast.success("Medication identified");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not analyze photo");
    } finally {
      setAnalyzing(false);
    }
  };

  const saveScan = () => {
    if (!scanResult?.name) return;
    onAdd(scanResult);
    setOpen(false); reset();
  };

  const saveManual = () => {
    if (!manualName.trim()) { toast.error("Enter a medication name"); return; }
    onAdd({ name: manualName.trim(), category: manualCategory });
    setOpen(false); reset();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm border border-dashed transition-colors ${triggerClass}`}
          data-testid={`${testidPrefix}-add-dialog-trigger`}
        >
          <PlusCircle className="w-3.5 h-3.5"/>
          {hasItems ? triggerLabelMore : triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-sand-50">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">{title}</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="bg-white border border-stone-100 rounded-full p-1 w-full grid grid-cols-2" data-testid={`${testidPrefix}-dialog-tabs`}>
            <TabsTrigger value="scan" className={`rounded-full ${activeTabClass}`} data-testid={`${testidPrefix}-tab-scan`}>
              <Sparkles className="w-3.5 h-3.5 mr-1.5"/>Scan
            </TabsTrigger>
            <TabsTrigger value="manual" className={`rounded-full ${activeTabClass}`} data-testid={`${testidPrefix}-tab-manual`}>
              Manual
            </TabsTrigger>
          </TabsList>

          {/* SCAN TAB */}
          <TabsContent value="scan" className="mt-4 space-y-3">
            {!previewUrl ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => cameraRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center transition-colors ${scanCardClass}`}
                  data-testid={`${testidPrefix}-scan-camera-btn`}
                >
                  <Camera className={`w-7 h-7 ${scanIconClass}`}/>
                  <p className={`mt-3 text-sm font-medium ${scanTextClass}`}>Take a photo</p>
                  <p className="mt-0.5 text-xs text-stone-500">Use rear camera</p>
                </button>
                <button
                  onClick={() => galleryRef.current?.click()}
                  className="border-2 border-dashed border-stone-300 bg-stone-50 hover:bg-stone-100 rounded-2xl p-6 flex flex-col items-center justify-center transition-colors"
                  data-testid={`${testidPrefix}-scan-gallery-btn`}
                >
                  <Upload className="w-7 h-7 text-stone-500"/>
                  <p className="mt-3 text-sm font-medium text-stone-700">Choose photo</p>
                  <p className="mt-0.5 text-xs text-stone-500">From device</p>
                </button>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files)} data-testid={`${testidPrefix}-scan-camera-input`}/>
                <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleFile(e.target.files)} data-testid={`${testidPrefix}-scan-gallery-input`}/>
              </div>
            ) : (
              <div className="space-y-3">
                <img src={previewUrl} alt="" className="w-full h-44 object-cover rounded-xl" data-testid={`${testidPrefix}-scan-preview`}/>

                {analyzing ? (
                  <div className="flex items-center justify-center gap-2 p-4 bg-white border border-stone-100 rounded-xl text-sm text-stone-600" data-testid={`${testidPrefix}-scan-analyzing`}>
                    <Loader2 className={`w-4 h-4 animate-spin ${scanIconClass}`}/>Analyzing with Claude Vision…
                  </div>
                ) : scanResult ? (
                  <div className="bg-white border border-stone-100 rounded-xl p-4 space-y-3" data-testid={`${testidPrefix}-scan-result`}>
                    <div>
                      <Label htmlFor={`${testidPrefix}-scan-name`} className="text-[11px] uppercase tracking-widest text-stone-500">Name</Label>
                      <Input id={`${testidPrefix}-scan-name`} value={scanResult.name} onChange={(e) => setScanResult({ ...scanResult, name: e.target.value })} className="mt-1 bg-stone-50 border-stone-200 rounded-xl" data-testid={`${testidPrefix}-scan-name-input`}/>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-widest text-stone-500">Category</Label>
                      <Select value={scanResult.category} onValueChange={(v) => setScanResult({ ...scanResult, category: v })}>
                        <SelectTrigger className="mt-1 bg-stone-50 border-stone-200 rounded-xl" data-testid={`${testidPrefix}-scan-category-select`}><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {TREATMENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : null}

                <div className="flex justify-between gap-2">
                  <Button variant="outline" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); setScanResult(null); }} className="rounded-full border-stone-300" data-testid={`${testidPrefix}-scan-retake-btn`}>
                    Retake
                  </Button>
                  <Button onClick={saveScan} disabled={!scanResult?.name || analyzing} className={`rounded-full ${primaryClass}`} data-testid={`${testidPrefix}-scan-save-btn`}>
                    Add to list
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* MANUAL TAB */}
          <TabsContent value="manual" className="mt-4 space-y-3">
            <div>
              <Label htmlFor={`${testidPrefix}-manual-name`}>Medication name</Label>
              <Input
                id={`${testidPrefix}-manual-name`}
                placeholder="e.g., Hydrocortisone 1% cream"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                className="mt-1.5 bg-white border-stone-200 rounded-xl"
                data-testid={`${testidPrefix}-manual-name-input`}
                autoFocus
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={manualCategory} onValueChange={setManualCategory}>
                <SelectTrigger className="mt-1.5 bg-white border-stone-200 rounded-xl" data-testid={`${testidPrefix}-manual-category-select`}><SelectValue/></SelectTrigger>
                <SelectContent>
                  {TREATMENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={saveManual} disabled={!manualName.trim()} className={`rounded-full ${primaryClass}`} data-testid={`${testidPrefix}-manual-save-btn`}>
                Add to list
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
